import { JsonCodeBlock } from '@/components/JsonCodeBlock'
import type { ToolCall, ToolCallEvent, ToolCallRequest } from '../../types'

interface ToolCallGroup {
  call: ToolCall
  result: string | null
}

function isToolCallRequest(event: ToolCallEvent): event is ToolCallRequest {
  return event.role === 'assistant'
}

function groupToolEvents(events: ToolCallEvent[]): ToolCallGroup[] {
  const resultsByCallId: Record<string, string> = {}
  for (const event of events) {
    if (!isToolCallRequest(event) && event.tool_call_id) {
      resultsByCallId[event.tool_call_id] = event.content ?? ''
    }
  }

  return events.flatMap((event) =>
    isToolCallRequest(event)
      ? event.tool_calls.map((call) => ({
          call,
          result: resultsByCallId[call.id] ?? null,
        }))
      : []
  )
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

function ToolCallJsonRow({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex gap-2 px-2.5 py-1.5">
      <span className="shrink-0 pt-px text-[10px] font-semibold uppercase tracking-wide text-neutral-low-contrast">
        {label}
      </span>
      <div className="min-w-0 flex-1 overflow-hidden rounded bg-gray-50 p-2">
        <JsonCodeBlock value={value} label={`Tool ${label}`} />
      </div>
    </div>
  )
}

export function ToolCallEvents({ events }: { events: ToolCallEvent[] }) {
  const groups = groupToolEvents(events)
  if (groups.length === 0) return null

  return (
    <div className="mt-2 space-y-1.5">
      {groups.map(({ call, result }) => {
        const hasArguments =
          call.function.arguments.trim() && call.function.arguments !== '{}'
        const resultValue =
          result !== null && result.trim() ? parseJson(result) : '(no result)'

        return (
          <div
            key={call.id}
            className="overflow-hidden rounded border border-gray-200 bg-white font-mono text-xs">
            <div className="flex items-baseline gap-2 border-b border-gray-100 px-2.5 py-1.5">
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-neutral-low-contrast">
                Tool
              </span>
              <span className="font-semibold text-primary-solid">
                {call.function.name}
              </span>
            </div>
            {hasArguments ? (
              <ToolCallJsonRow
                label="In"
                value={parseJson(call.function.arguments)}
              />
            ) : null}
            {result !== null ? (
              <ToolCallJsonRow label="Out" value={resultValue} />
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
